import React from 'react';
import { Search, FileText, Clock, CheckCircle, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const DocumentRetrievalPage = () => {
  const documentRetrievalSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Document Retrieval Services California",
    "description": "Professional document retrieval from California courts, government agencies, and public records. Fast, reliable service for legal professionals and businesses.",
    "provider": {
      "@type": "LegalService",
      "name": "Legal Document Specialist",
      "telephone": "(760) 866-9182",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "225 South Civic Drive, Ste 2-16",
        "addressLocality": "Palm Springs",
        "addressRegion": "CA",
        "postalCode": "92262",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "availableLanguage": "English",
      "servicePhone": "(760) 866-9182",
      "serviceUrl": `${window.location.origin}/document-retrieval`
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Court Document Retrieval",
        "description": "Retrieval of court records and case files",
      },
      {
        "@type": "Offer",
        "name": "Public Records Search",
        "description": "Government agency document retrieval",
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does document retrieval typically take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Turnaround times vary depending on the urgency level and document type. Standard service takes 3-5 business days, priority service 1-2 business days, and rush same-day service is available for urgent matters. Complex records or hard-to-locate documents may require additional time."
        }
      },
      {
        "@type": "Question",
        "name": "What types of documents can you retrieve?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We retrieve all types of legal and public documents including court records (civil, criminal, family), case files, judgments, property records, business filings, vital records (birth, death, marriage certificates), license records, and government agency documents throughout California."
        }
      },
      {
        "@type": "Question",
        "name": "How much does document retrieval cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pricing varies based on document type, urgency, and complexity. Email your case specifics to quote@legaldocspecialist.com for a quote based on your specific needs."
        }
      },
      {
        "@type": "Question",
        "name": "Are your document retrieval services confidential?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. We maintain strict confidentiality protocols including encrypted transmission, secure portals, HIPAA compliance for medical records, and comprehensive data privacy safeguards. All staff members sign confidentiality agreements and undergo background checks."
        }
      },
      {
        "@type": "Question",
        "name": "Can you retrieve documents from any California county?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we provide document retrieval services throughout all 58 California counties. Our strategically located teams and established relationships with county clerks and government agencies ensure efficient retrieval regardless of location."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to provide authorization for medical record retrieval?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, for medical records and other protected documents, proper authorization forms must be provided. We can assist with drafting subpoenas, obtaining signed authorizations, and ensuring HIPAA compliance throughout the retrieval process."
        }
      }
    ]
  };

  React.useEffect(() => {
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.textContent = JSON.stringify(documentRetrievalSchema);
    document.head.appendChild(serviceScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(documentRetrievalSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="Document Retrieval California | Court Records & Public Documents | Legal Document Specialist"
        description="Professional document retrieval services throughout California. Court records, public documents, government files. Fast, reliable service. Request a quote online."
        keywords="document retrieval California, court records search, public records California, legal document search, government documents, case file retrieval"
        canonical="/document-retrieval"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              California Document Retrieval Services
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional document retrieval from California courts, government agencies, and public records. Fast, reliable service for legal professionals and businesses.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/quote?service=document-retrieval" className="btn-primary" data-testid="hero-quote-link">
                Get Document Retrieval Quote
              </Link>
              <a href="mailto:support@legaldocspecialist.com" className="btn-secondary">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Document Retrieval Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Comprehensive document retrieval from multiple sources throughout California
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                name: 'Court Records',
                icon: <FileText size={28} color="white" />,
                description: 'Civil, criminal, family court records and case files',
                features: ['Case files and dockets', 'Judgments and orders', 'Court transcripts', 'Filing records']
              },
              {
                name: 'Public Records',
                icon: <Search size={28} color="white" />,
                description: 'Government agency documents and public filings',
                features: ['Property records', 'Business filings', 'License records', 'Permits and applications']
              },
              {
                name: 'Vital Records',
                icon: <FileText size={28} color="white" />,
                description: 'Birth, death, marriage, and divorce certificates',
                features: ['Birth certificates', 'Death certificates', 'Marriage records', 'Divorce decrees']
              },
              {
                name: 'Research Services',
                icon: <Search size={28} color="white" />,
                description: 'Comprehensive research and document compilation',
                features: ['Asset searches', 'Background checks', 'Historical records', 'Document authentication']
              }
            ].map((service, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  {service.icon}
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                  {service.name}
                </h3>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem'
                }}>
                  {service.description}
                </p>
                
                <ul style={{
                  textAlign: 'left',
                  paddingLeft: '0',
                  listStyle: 'none'
                }}>
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <CheckCircle size={16} color="var(--brand-primary)" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Turnaround Times */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Service Turnaround Times
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Flexible timelines to meet your research and retrieval needs
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                name: 'Standard Service',
                timeline: '3-5 Business Days',
                description: 'Regular document retrieval for non-urgent matters'
              },
              {
                name: 'Priority Service',
                timeline: '1-2 Business Days',
                description: 'Expedited retrieval for time-sensitive requests'
              },
              {
                name: 'Rush Service',
                timeline: 'Same Day',
                description: 'Same-day document retrieval for urgent needs'
              }
            ].map((service, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Clock size={24} color="white" />
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                  {service.name}
                </h3>
                
                <Link to="/quote?service=document-retrieval" data-testid={`quote-link-${index}`} style={{ display: 'inline-block', fontSize: '0.95rem', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '0.5rem', textDecoration: 'underline' }}>Request a Quote</Link>
                
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  marginBottom: '1rem'
                }}>
                  {service.timeline}
                </p>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)'
                }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Professional Document Retrieval?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Streamline your legal workflow with expert document retrieval services
            </p>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              In today's fast-paced legal environment, efficient document retrieval is crucial for law firms and legal professionals. Our specialized document retrieval services provide California law firms with secure, fast access to court records, public documents, and government files. We utilize HIPAA-compliant portals, advanced technology, and strategically located teams throughout California to expedite requests and deliver digital copies with optional analysis and summarization.
            </p>
            
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Professional document retrieval reduces turnaround times drastically, minimizes errors and delays caused by manual processes, and lowers overhead costs. This allows attorneys to focus more on legal strategy rather than administrative tasks. Our secure digital delivery methods and encryption protect sensitive client information, maintaining compliance with state and federal regulations including the California Public Records Act (PRA).
            </p>
          </div>
        </div>
      </section>

      {/* The Retrieval Process */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Our Document Retrieval Process
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              A streamlined, professional approach from request to delivery
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {[
              {
                step: '1',
                title: 'Request Submission',
                description: 'Submit your document retrieval request through our secure portal with case details and specific document requirements.'
              },
              {
                step: '2',
                title: 'Verification & Research',
                description: 'Our team verifies access requirements, identifies the correct jurisdiction, and locates the specific documents needed.'
              },
              {
                step: '3',
                title: 'Document Retrieval',
                description: 'We retrieve documents from court clerks, county recorders, or government agencies following all legal protocols.'
              },
              {
                step: '4',
                title: 'Secure Delivery',
                description: 'Documents are digitized, encrypted, and delivered through our secure portal with status updates and download access.'
              }
            ].map((item, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: 'white'
                }}>
                  {item.step}
                </div>
                <h3 className="heading-4" style={{ marginBottom: '1rem' }}>
                  {item.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance & Security */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Compliance & Security Standards
            </h2>
            
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Legal Compliance Under California Law
              </h3>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.8'
              }}>
                Our document retrieval services fully comply with the California Public Records Act (PRA) and all applicable state and federal regulations. We understand the critical importance of proper exemption analysis, timely responses to requests, and comprehensive data privacy safeguards. Our team maintains strict adherence to legal protocols to minimize litigation risks for our clients.
              </p>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: '1.8'
              }}>
                We work closely with legal counsel to ensure strategic compliance, including policy drafting support and PRA training. All document access procedures are designed to protect sensitive information while ensuring authorized parties receive the records they need for legitimate legal purposes.
              </p>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Data Security & Privacy Protection
              </h3>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.8'
              }}>
                We employ enterprise-grade security measures including HIPAA-compliant portals, encrypted transmission protocols, and secure chain-of-custody procedures. All retrieved documents are handled with the highest level of confidentiality, and our team undergoes regular training on data privacy and security best practices.
              </p>
              <ul style={{
                paddingLeft: '0',
                listStyle: 'none'
              }}>
                {[
                  'End-to-end encryption for all digital transmissions',
                  'Secure 24/7 portal access with multi-factor authentication',
                  'HIPAA and GLBA compliance for sensitive records',
                  'Detailed audit trails and access logging',
                  'Regular security assessments and compliance reviews',
                  'Staff background checks and confidentiality agreements'
                ].map((feature, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <CheckCircle size={20} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Innovation */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Technology-Driven Document Retrieval
            </h2>
            
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              textAlign: 'center',
              lineHeight: '1.8'
            }}>
              Our advanced technology platform provides law firms with efficient, accurate, and secure document retrieval services. We leverage cloud-based solutions, AI-powered workflows, and proprietary databases to streamline the entire retrieval process.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              {[
                {
                  title: 'Online Portal Access',
                  description: '24/7 access to request documents, track status, view updates, and download retrieved records instantly from anywhere.'
                },
                {
                  title: 'Automated Workflows',
                  description: 'AI-powered automation reduces manual processing, eliminates redundant tasks, and improves accuracy throughout the retrieval process.'
                },
                {
                  title: 'Real-Time Tracking',
                  description: 'Monitor your document requests with live status updates, estimated completion times, and instant notifications.'
                },
                {
                  title: 'Digital Document Management',
                  description: 'All retrieved documents are professionally digitized, OCR-processed for searchability, and organized for easy access.'
                },
                {
                  title: 'Integration Capabilities',
                  description: 'Seamless integration with popular practice management software including Clio, NetDocuments, and iManage.'
                },
                {
                  title: 'Advanced Search Tools',
                  description: 'Powerful search functionality across multiple jurisdictions and record types to locate exactly what you need.'
                }
              ].map((feature, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {feature.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
              Explore Our Legal Document Services
            </h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              Document retrieval is just one of many essential services for California law firms. Learn about our complete suite of legal document solutions.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/resources/legal-document-services-guide"
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                View Complete Services Guide →
              </Link>
              <Link 
                to="/court-filing"
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: 'transparent',
                  color: 'var(--brand-primary)',
                  border: '2px solid var(--brand-primary)',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Court Filing Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'How long does document retrieval typically take?',
                answer: 'Turnaround times vary depending on the urgency level and document type. Standard service takes 3-5 business days, priority service 1-2 business days, and rush same-day service is available for urgent matters. Complex records or hard-to-locate documents may require additional time.'
              },
              {
                question: 'What types of documents can you retrieve?',
                answer: 'We retrieve all types of legal and public documents including court records (civil, criminal, family), case files, judgments, property records, business filings, vital records (birth, death, marriage certificates), license records, and government agency documents throughout California.'
              },
              {
                question: 'How much does document retrieval cost?',
                answer: 'Pricing varies based on document type, urgency, and complexity. Email your case specifics to quote@legaldocspecialist.com for a quote based on your specific needs.'
              },
              {
                question: 'Are your document retrieval services confidential?',
                answer: 'Absolutely. We maintain strict confidentiality protocols including encrypted transmission, secure portals, HIPAA compliance for medical records, and comprehensive data privacy safeguards. All staff members sign confidentiality agreements and undergo background checks.'
              },
              {
                question: 'Can you retrieve documents from any California county?',
                answer: 'Yes, we provide document retrieval services throughout all 58 California counties. Our strategically located teams and established relationships with county clerks and government agencies ensure efficient retrieval regardless of location.'
              },
              {
                question: 'Do I need to provide authorization for medical record retrieval?',
                answer: 'Yes, for medical records and other protected documents, proper authorization forms must be provided. We can assist with drafting subpoenas, obtaining signed authorizations, and ensuring HIPAA compliance throughout the retrieval process.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need Document Retrieval Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Professional document retrieval from courts and government agencies throughout California
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/place-order"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Place Order Now
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DocumentRetrievalPage;